import React, { useState } from 'react'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { Board } from '../campaign/components/board/board'
import { ViewCampaign } from '../campaign/components/view-campaign'
import { useCampaign } from '../campaign/hooks'
import { useTranslation } from 'react-i18next'
import { CandidateCreateModal } from '../candidate/components/candidate-create-modal'
import { PortaledBreadcrumb } from '../components/portaled-breadcrumb'
import { CandidateArchiveDrawer } from '../candidate/components/candidate-archive-drawer'
import { Toolbar } from '../campaign/components/board/toolbar'
import { useTitle } from '../hooks/use-title'
import { SHOW_CAMPAIGN } from '../user/permission'

export const Route = createFileRoute('/_auth/_main/campaigns/$campaignId/board')({
    validateSearch: z.object({
        candidateId: z.string().optional(),
        bulk: z.boolean().optional(),
        create: z.boolean().optional(),
        archive: z.boolean().optional(),
    }),
    beforeLoad: ({ context }) => {
        if (!context.auth.can(SHOW_CAMPAIGN)) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    component: RouteComponent,
})

export default function RouteComponent() {
    const { campaignId } = Route.useParams()
    const { candidateId } = Route.useSearch()
    const { t } = useTranslation()
    const { bulk, create, archive } = Route.useSearch()
    const navigate = useNavigate()
    const [selected, setSelected] = useState<Record<string, boolean>>({})
    const campaign = useCampaign(campaignId)

    useTitle(campaign.data?.title)

    if (campaign.isPending || campaign.isError) {
        return null
    }

    return (
        <ViewCampaign
            pageHeader={{
                suffix: (
                    <Toolbar
                        campaign={campaign.data!}
                        selected={selected}
                        setSelected={setSelected}
                    />
                ),
            }}
            campaign={campaign.data}
            candidateImportModal={{
                open: bulk,
                onCancel: () => {
                    navigate({
                        to: '.',
                        search: {},
                    })
                },
            }}
        >
            <PortaledBreadcrumb
                items={[
                    {
                        title: <Link to="/campaigns">{t('Campaigns')}</Link>,
                    },
                    {
                        title: campaign.data.title,
                    },
                ]}
            />
            <CandidateArchiveDrawer
                open={archive}
                onClose={() => {
                    navigate({
                        to: '.',
                        search(search) {
                            return {
                                ...search,
                                archive: undefined,
                            }
                        },
                    })
                }}
                campaignId={campaignId}
            />
            <Board
                campaignId={campaign.data.id}
                columns={campaign.data.columns || []}
                candidateId={candidateId}
                height="60vh"
                selected={selected}
                setSelected={setSelected}
            />
            <CandidateCreateModal
                onCancel={() => {
                    navigate({
                        to: '.',
                        search: (search) => {
                            return {
                                ...search,
                                create: undefined,
                            }
                        },
                    })
                }}
                open={create}
                campaignId={campaignId}
            />
        </ViewCampaign>
    )
}
